import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'

const resource = 'evaluationSummarys'

interface IListSummariesQuery {
  search?: string
  page?: number
  limit?: number
  formularId?: string | string[]
  resourceId?: string | string[]
  sharedWithId?: string | string[]
  evaluationId?: string
}

const listSummaries = (axios: IFactoryFunctionRequest<any, any>) => (query: IListSummariesQuery = {}): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    params: query,
  })
}

const createSummary = (axios: IFactoryFunctionRequest<any, any>) => (data): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

const update = (axios: IFactoryFunctionRequest<any, any>) => (summaryId, data): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}`,
    data,
  })
}

const remove = (axios: IFactoryFunctionRequest<{ message: string }, void>) => (summaryId: string): IFactoryResponse<{ message: string }> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}`,
  })
}

const retreiveSummary = (axios: IFactoryFunctionRequest<any, any>) => (summaryId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}`,
  })
}

const downloadPdf = (axios: IFactoryFunctionRequest<any, any>) => (summaryId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/summaryPdf/${summaryId}`,
  })
}

const listDisplayedOnEvaluation = (axios: IFactoryFunctionRequest<any, any>) => (evaluationId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/displayed`,
  })
}

const retreiveQuickCreateEvaluations = (axios: IFactoryFunctionRequest<any, any>) => (evaluationId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/quick-create-evaluations`,
  })
}

// Commented out: per-item AI generation replaced by generateAll
// const generateSummaryItem = (axios: IFactoryFunctionRequest<any, any>) => (summaryId, columnId, summaryItemId): IFactoryResponse<any, any> => {
//   return axios({
//     method: EMethod.patch,
//     url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}/${columnId}/${summaryItemId}/generate`,
//   })
// }

const generateAll = (axios: IFactoryFunctionRequest<any, any>) => (summaryId: string, query: { async?: boolean } = {}): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}/generate-all`,
    query,
  })
}

interface IUpdateColumnPayload {
  summary?: string
  cloudTags?: { tag: string; weight: number; positiveFeedBack?: number }[]
  answers?: { _id: string; value?: any; positiveFeedBack?: number; anonymized?: boolean }[]
}

const updateColumn = (axios: IFactoryFunctionRequest<any, any>) => (summaryId: string, columnId: string, summaryItemId: string, data: IUpdateColumnPayload): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}/${columnId}/${summaryItemId}`,
    data,
  })
}

interface ISharedWith {
  resourceId: string
  tillDate?: string | Date
}

const publish = (axios: IFactoryFunctionRequest<any, any>) => (summaryId: string, data: { isVisible: boolean; sharedWith?: ISharedWith[] }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}/publish`,
    data,
  })
}

const anonymize = (axios: IFactoryFunctionRequest<any, any>) => (summaryId: string, data: { anonymized: boolean }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}/anonymize`,
    data,
  })
}

const updateAnswerAnonymized = (axios: IFactoryFunctionRequest<any, any>) => (
  summaryId: string,
  columnId: string,
  summaryItemId: string,
  data: { answerId: string; anonymized: boolean }
): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${summaryId}/${columnId}/${summaryItemId}/anonymized`,
    data,
  })
}

export default (axios: AxiosInstance) => ({
  listSummaries: listSummaries(useAxios(axios)),
  createSummary: createSummary(useAxios(axios)),
  retreiveSummary: retreiveSummary(useAxios(axios)),
  downloadPdf: downloadPdf(useAxios(axios)),
  listDisplayedOnEvaluation: listDisplayedOnEvaluation(useAxios(axios)),
  retreiveQuickCreateEvaluations: retreiveQuickCreateEvaluations(useAxios(axios)),
  // generateSummaryItem: generateSummaryItem(useAxios(axios)), // Commented out: replaced by generateAll
  generateAll: generateAll(useAxios(axios)),
  updateColumn: updateColumn(useAxios(axios)),
  publish: publish(useAxios(axios)),
  anonymize: anonymize(useAxios(axios)),
  updateAnswerAnonymized: updateAnswerAnonymized(useAxios(axios)),
  update: update(useAxios(axios)),
  remove: remove(useAxios(axios)),
})
